'use client';

import { useState, useEffect } from 'react';
import { FetchApiClient } from '@shared/httpService';
import { Logger } from '@shared/logger';
import { tryCatchAsync } from '@shared/utils';
import { HTTP_STATUS_CODES } from '@shared/constants';
import type { PatientProfileCreateResponse } from '../types/patientprofile/PatientProfileCreateResponse';

/**
 * @group Hooks
 * 
 * @summary
 * <blockquote>Custom React hook to fetch patient profile data, handle loading states, and provide refetch functionality.</blockquote>
 * 
 * @example
 * ```typescript
 * const { profile, loading, error, refetch } = usePatientProfile();
 * 
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!profile) return <CreateProfileForm />;
 * return <ProfileView profile={profile} />;
 * ```
 */
export function usePatientProfile() {
  const [profile, setProfile] = useState<PatientProfileCreateResponse['profile'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);

    const wrappedResult = await tryCatchAsync(async () => {
      const apiClient = new FetchApiClient();
      const result = await apiClient.get('/api/data/patient-profiles');

      if (result.isErr) {
        const errorData = result.getFail();
        
        // Handle 404 as "no profile exists" (not an error)
        if (errorData.errorCode === 'NOT_FOUND' || (errorData as unknown as { status: number }).status === HTTP_STATUS_CODES.NOT_FOUND) {
          Logger.debug('[usePatientProfile] No patient profile found');
          return { profile: null, hasError: false };
        }
        
        // Handle other errors
        Logger.error('[usePatientProfile] Error fetching patient profile', { 
          resource: 'patient.profile',
          errorCode: errorData.errorCode,
        }, new Error(errorData.errorMessage));
        return { profile: null, hasError: true, errorMessage: errorData.errorMessage || 'Failed to fetch patient profile' };
      }

      const apiResponse = result.getSuccess();
      
      if (!apiResponse.ok) {
        // Handle 404 as "no profile exists" (not an error)
        if (apiResponse.status === HTTP_STATUS_CODES.NOT_FOUND) {
          Logger.debug('[usePatientProfile] No patient profile found');
          return { profile: null, hasError: false };
        }
        
        // Handle other HTTP error responses
        const errorMessage = `Failed to fetch patient profile. Status: ${apiResponse.status}`;
        Logger.error('[usePatientProfile] API error', { resource: 'patient.profile' }, new Error(errorMessage));
        return { profile: null, hasError: true, errorMessage };
      }

      const data = apiResponse.data as PatientProfileCreateResponse;
      
      if (data && data.profile) {
        Logger.debug('[usePatientProfile] Patient profile loaded successfully');
        return { profile: data.profile, hasError: false };
      }
      
      Logger.debug('[usePatientProfile] No patient profile in response');
      return { profile: null, hasError: false };
    });

    wrappedResult.match({
      ok: (data) => {
        setProfile(data.profile);
        setError(data.hasError ? data.errorMessage ?? null : null);
      },
      err: (error) => {
        Logger.error('[usePatientProfile] Unexpected error', { 
          resource: 'patient.profile',
        }, new Error(error.errorMessage));
        setError('An unexpected error occurred while fetching patient profile');
        setProfile(null);
      }
    });

    setLoading(false);
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return { profile, loading, error, refetch: fetchProfile };
}

